import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Gavel, Clock, MapPin, TrendingUp, Shield, Trophy, ChevronRight, Zap } from "lucide-react";
import { toast } from "sonner";
import { auctionsAPI, resolveImg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function useCountdown(iso) {
  const [left, setLeft] = useState(iso ? new Date(iso).getTime() - Date.now() : 0);
  useEffect(() => {
    if (!iso) return;
    const t = setInterval(() => setLeft(new Date(iso).getTime() - Date.now()), 1000);
    return () => clearInterval(t);
  }, [iso]);
  if (left <= 0) return { expired: true, d:0, h:0, m:0, s:0 };
  const d = Math.floor(left / 86400000);
  const h = Math.floor((left % 86400000) / 3600000);
  const m = Math.floor((left % 3600000) / 60000);
  const s = Math.floor((left % 60000) / 1000);
  return { expired: false, d, h, m, s };
}

export default function AuctionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [auction, setAuction] = useState(null);
  const [bid, setBid] = useState("");
  const [loading, setLoading] = useState(false);
  const countdown = useCountdown(auction?.ends_at);

  useEffect(() => {
    auctionsAPI.get(id).then(setAuction).catch(() => setAuction(null));
  }, [id]);

  if (!auction) return <div className="p-16 text-center text-slate-500">Caricamento...</div>;

  const increment = Number(auction.min_increment || 3);
  const minBid = (auction.current_bid || 0) + increment;
  const buyNow = auction.buy_now_price ? Number(auction.buy_now_price) : null;
  const wonByBuyNow = auction.status === "won_buy_now";

  const placeBid = async () => {
    if (!user) {
      toast.info("Accedi per offrire");
      navigate("/login");
      return;
    }
    const n = Number(bid);
    if (!n || n < minBid) {
      toast.error(`L'offerta minima è €${minBid}`);
      return;
    }
    setLoading(true);
    try {
      const updated = await auctionsAPI.bid(id, n);
      setAuction(updated);
      setBid("");
      toast.success(`Offerta di €${n} accettata! Sei il miglior offerente.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore offerta");
    } finally {
      setLoading(false);
    }
  };

  const buyNowAction = async () => {
    if (!user) {
      toast.info("Accedi per acquistare");
      navigate("/login");
      return;
    }
    if (!buyNow) return;
    if (!window.confirm(`Confermi l'acquisto immediato a €${buyNow}? L'asta verrà chiusa subito a tuo favore.`)) return;
    setLoading(true);
    try {
      const updated = await auctionsAPI.buyNow(id);
      setAuction(updated);
      toast.success(`Hai vinto l'asta a €${buyNow}!`, { description: "Riceverai a breve il link di pagamento Stripe." });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore acquisto immediato");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <nav className="flex items-center gap-1 text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#e86a24]">Home</Link>
        <ChevronRight className="w-3 h-3"/>
        <Link to="/aste" className="hover:text-[#e86a24]">Aste</Link>
        <ChevronRight className="w-3 h-3"/>
        <span className="text-slate-900 font-medium">{auction.title}</span>
      </nav>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="aspect-[4/3] bg-slate-100">
            <img src={resolveImg(auction.image)} alt={auction.title} className="w-full h-full object-contain"/>
          </div>
          <div className="p-6">
            <Badge className="bg-[#e86a24] hover:bg-[#e86a24] auction-live mb-3"><Gavel className="w-3 h-3 mr-1"/>ASTA LIVE</Badge>
            <h1 className="text-2xl font-black text-[#1d3d3a]">{auction.title}</h1>
            <div className="mt-2 flex items-center gap-3 text-sm text-slate-500">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{auction.castello}</span>
              <span className="flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5"/>{auction.bid_count} offerte</span>
            </div>
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="text-xs text-slate-500 uppercase tracking-wider font-bold">Offerta attuale</div>
            <div className="text-5xl font-black text-[#e86a24] mt-1">€ {Number(auction.current_bid).toLocaleString("it-IT")}</div>
            {auction.top_bidder && <div className="text-xs text-slate-500 mt-1">Da {auction.top_bidder}</div>}

            <div className="mt-5 bg-[#1d3d3a] text-white rounded-xl p-4">
              <div className="text-xs text-slate-300 mb-2 flex items-center gap-1"><Clock className="w-3.5 h-3.5"/>Termina in</div>
              {countdown.expired ? (
                <div className="text-xl font-black text-[#e86a24]">ASTA TERMINATA</div>
              ) : (
                <div className="grid grid-cols-4 gap-2">
                  {[["G", countdown.d], ["H", countdown.h], ["M", countdown.m], ["S", countdown.s]].map(([k,v]) => (
                    <div key={k} className="bg-white/10 rounded-lg p-2 text-center">
                      <div className="text-xl font-black font-mono">{String(v).padStart(2, "0")}</div>
                      <div className="text-[10px] text-slate-300">{k}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5">
              <label className="text-xs font-bold text-slate-700 block mb-2">La tua offerta (min. €{minBid.toFixed(2)} · rialzo €{increment})</label>
              <div className="flex gap-2">
                <Input type="number" value={bid} onChange={(e) => setBid(e.target.value)} placeholder={String(minBid)} className="h-12 text-lg font-bold" disabled={wonByBuyNow}/>
                <Button onClick={placeBid} disabled={countdown.expired || loading || wonByBuyNow} className="h-12 bg-[#e86a24] hover:bg-[#c8561a] px-6 font-bold">
                  <Gavel className="w-4 h-4 mr-1"/>{loading ? "..." : "Offri"}
                </Button>
              </div>
              <div className="flex gap-2 mt-2">
                {[minBid, minBid+10, minBid+25].map(v => (
                  <button key={v} onClick={() => setBid(String(v))} className="flex-1 text-xs py-1.5 px-2 border border-slate-200 rounded-lg hover:border-[#e86a24]" disabled={wonByBuyNow}>
                    € {v}
                  </button>
                ))}
              </div>
            </div>

            {buyNow && !wonByBuyNow && !countdown.expired && (
              <div className="mt-4 bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5 text-amber-600 fill-amber-400" />
                  <div className="font-black text-amber-900 text-sm">Compralo subito</div>
                </div>
                <div className="text-2xl font-black text-amber-700 mb-3">€ {Number(buyNow).toLocaleString("it-IT")}</div>
                <Button onClick={buyNowAction} disabled={loading} className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-full" data-testid="auction-buy-now-button">
                  <Zap className="w-4 h-4 mr-2 fill-white" />
                  {loading ? "..." : `Acquista ora a €${buyNow}`}
                </Button>
                <div className="text-[11px] text-amber-900 mt-2">Chiudi l'asta immediatamente a tuo favore. Salti i rilanci.</div>
              </div>
            )}

            {wonByBuyNow && (
              <div className="mt-4 bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 flex items-start gap-3">
                <Trophy className="w-6 h-6 text-emerald-600 shrink-0"/>
                <div>
                  <div className="font-black text-emerald-900">Asta aggiudicata con Compralo subito</div>
                  <div className="text-xs text-emerald-800 mt-1">Importo: €{Number(auction.current_bid).toLocaleString("it-IT")}. Pagamento Stripe in arrivo.</div>
                </div>
              </div>
            )}

            <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <Shield className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>Offerte vincolanti. In caso di vittoria, pagamento protetto tramite Stripe entro 24h.</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <Trophy className="w-8 h-8 text-amber-600 shrink-0"/>
              <div>
                <div className="font-black text-amber-900">Se vinci, paghi subito</div>
                <div className="text-xs text-amber-800 mt-1">Una volta vinta l'asta, riceverai il link Stripe per saldare entro 24h.</div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
